/**
 * Diffs a set of live aiWARE config definitions against the repo manifest.
 *
 * Pure and Redux-free on purpose: this is the part of the /admin/app-config
 * page whose correctness matters most and which is cheapest to pin with unit
 * tests, so it takes plain data in and returns plain data out. The saga fetches, this
 * decides, the page renders.
 *
 * Bucketing is by key, and every live key lands in exactly one bucket:
 *   - in `definitions`/`advanced`/`developer` -> inSync or drifted (missing when absent)
 *   - in `deprecated`                 -> deprecatedPresent (it should not exist)
 *   - in none of them                 -> unrecognized
 *
 * All three definition lists are diffed IDENTICALLY — bucketing does not know
 * about tiers, and a registered key is compared the same way wherever it is
 * listed. What the lists carry is `tierByKey` on the result, and the PAGE
 * decides what an absence means with it: absent `standard`/`advanced` is a
 * finding, absent `developer` is a knob nobody asked for. Keeping that decision
 * out of here is deliberate — the diff reports what it sees, and how loudly to
 * say it is a presentation question.
 *
 * A live field that comes back null is recorded in `unknownFields`, not in
 * `differences`: we cannot conclude a definition drifted on a value the API
 * declined to give us, and doing so would mark a whole environment drifted.
 *
 * Each difference also records whether Update can write it. The platform drops
 * falsy values on that mutation and reports success anyway, so a row whose
 * drift cannot be repaired must not be offered a button that says otherwise.
 *
 * DRIFT IS NARROWER THAN DIFFERENCE, twice over.
 *
 * By field: only `configType`, `defaultValue` and `defaultValueJSON` are
 * compared at all — see COMPARABLE_FIELDS. `configLevel`, `required`,
 * `secured` and `description` are shown in the detail view and never bucket a
 * key as drifted, because none of them changes what any code reads and a
 * wording difference is not a reason to offer to write to a live environment.
 *
 * And by type: a `defaultValueJSON` difference only counts when the definition
 * is `"JSON"`-typed — see `isMaterial`. The platform stores that column for
 * every configType and reads it for one. An immaterial difference is still
 * carried on the record so the detail view can mark it, in a colour that is not
 * the drift red; it just does not move the key or block Update.
 */
import { isEqual, isNil } from 'lodash';

import {
  COMPARABLE_FIELDS,
  ComparableField,
  ComparedDefinition,
  ConfigDefinitionManifest,
  DefinitionDiff,
  DefinitionTier,
  DeprecatedPresent,
  FieldDifference,
  LiveDefinition,
  ManifestDefinition,
} from '.';

/**
 * Whether `applicationConfigDefinitionUpdate` would actually write this value.
 *
 * The DAL assembles its SET clause with truthy checks, so anything falsy is
 * dropped while the mutation still reports success. Note that the string
 * `"false"` and the string `"0"` ARE truthy and do get written — it is the
 * boolean `false`, `null`/`undefined` and `""` that vanish.
 *
 * Must stay in step with `toUpdateFields` in ../services: this predicts what
 * that function's output will do, so if it starts sending a field differently
 * this rule has to follow.
 */
export function isWritableByUpdate(
  value: string | boolean | undefined
): boolean {
  return Boolean(value);
}

/**
 * A JSON value rendered as a stable string, with object keys sorted.
 *
 * Both a comparison and a display form. Sorted because the platform does not
 * promise key order and `{"a":1,"b":2}` is not a drift from `{"b":2,"a":1}` —
 * comparing raw JSON.stringify output would report one, every scan, with no way
 * for an operator to make it stop. `null`/`undefined` collapse to `undefined`,
 * which is how every other field here says "declares none".
 */
const sortDeep = (input: unknown): unknown => {
  if (Array.isArray(input)) {
    return input.map(sortDeep);
  }
  if (input && typeof input === 'object') {
    const entries = Object.keys(input as Record<string, unknown>)
      .sort()
      .map((key) => [key, sortDeep((input as Record<string, unknown>)[key])]);
    return Object.fromEntries(entries);
  }
  return input;
};

export function canonicalJson(value: unknown): string | undefined {
  if (isNil(value)) {
    return undefined;
  }
  return JSON.stringify(sortDeep(value));
}

/**
 * The same value, indented, for DISPLAY ONLY.
 *
 * Deliberately not what `canonicalJson` returns. That one is the comparison
 * key — a single line, so two equal blobs are one `isEqual` away — and
 * whitespace in it would make every diff depend on how it was formatted.
 * This one is what a person reads: a transcription-engine options blob on one
 * line is a wall of quotes and commas nobody can check against another.
 */
export function prettyJson(value: unknown): string | undefined {
  if (isNil(value)) {
    return undefined;
  }
  return JSON.stringify(sortDeep(value), null, 2);
}

/** Reads a comparable field off the manifest side, normalising "declares none". */
function manifestValue(
  definition: ManifestDefinition,
  field: ComparableField
): string | boolean | undefined {
  if (field === 'defaultValueJSON') {
    return canonicalJson(definition.defaultValueJSON);
  }
  return definition[field];
}

/**
 * Reads a comparable field off a live record. `null` and `undefined` both mean
 * "not supplied" and collapse to `undefined`, so callers only test one thing.
 */
function liveValue(
  live: LiveDefinition,
  field: ComparableField
): string | boolean | undefined {
  if (field === 'defaultValueJSON') {
    return canonicalJson(live.defaultValueJSON);
  }
  const value = live[field];
  return value === null ? undefined : value;
}

/**
 * The two fields where absence is itself comparable.
 *
 * Everywhere else a null from the API means "not supplied" and nothing can be
 * concluded. For the defaults the manifest genuinely distinguishes "declares
 * none" from a value, so an environment that has one where we expect none HAS
 * drifted, and must not be filed under "we could not tell".
 */
const ABSENCE_IS_COMPARABLE: readonly ComparableField[] = [
  'defaultValue',
  'defaultValueJSON',
];

/**
 * `{}` and "declares none" are the same claim about a default.
 *
 * The platform hands back an empty object for a `defaultValueJSON` nobody set,
 * so an environment reading `{}` where the manifest declares none is reporting
 * the absence we expect, in its own spelling. Treating that as a change put a
 * permanent Changed badge on rows where nothing had been changed. The detail
 * view still shows both spellings when expanded — they are not identical, just
 * not a finding.
 */
export function isEmptyJsonDefault(
  value: string | boolean | undefined
): boolean {
  return value === undefined || value === '{}';
}

/**
 * Whether a difference in this field actually decides anything.
 *
 * Only `defaultValueJSON` is ever immaterial, and only because the platform
 * stores a column it does not always use: `defaultValueJSON` is where a
 * JSON-typed config keeps its default, and for every OTHER configType nothing
 * reads it. A `String` definition carrying a stray JSON blob is a curiosity,
 * not a misconfiguration — bucketing it as Drifted would send an operator to
 * repair a value no code consults, and Update cannot clear it anyway.
 *
 * EITHER side being `"JSON"` makes it material: if the manifest says JSON the
 * environment ought to have a JSON default, and if the environment says JSON
 * its blob is live data whatever we expected.
 *
 * `"JSON"` is the ApplicationConfigValueEnum member, matched exactly — the
 * enum is uppercase and the API does not accept any other spelling.
 *
 * An empty object against "declares none" is immaterial WHATEVER the
 * configType, JSON included — see `isEmptyJsonDefault`.
 */
function isMaterial(
  field: ComparableField,
  definition: ManifestDefinition,
  live: LiveDefinition,
  expected: string | boolean | undefined,
  actual: string | boolean | undefined
): boolean {
  if (field !== 'defaultValueJSON') {
    return true;
  }
  if (isEmptyJsonDefault(expected) && isEmptyJsonDefault(actual)) {
    return false;
  }
  return definition.configType === 'JSON' || live.configType === 'JSON';
}

function compare(
  definition: ManifestDefinition,
  live: LiveDefinition
): ComparedDefinition {
  const differences: FieldDifference[] = [];
  const unknownFields: ComparableField[] = [];

  for (const field of COMPARABLE_FIELDS) {
    const expected = manifestValue(definition, field);
    const actual = liveValue(live, field);

    if (actual === undefined && !ABSENCE_IS_COMPARABLE.includes(field)) {
      unknownFields.push(field);
      continue;
    }

    if (!isEqual(expected, actual)) {
      differences.push({
        field,
        expected,
        actual,
        // What Update would send for this field is the manifest's value, and
        // for the defaults specifically `?? null` — so the manifest side is
        // exactly what decides whether the write lands.
        writable: isWritableByUpdate(expected),
        material: isMaterial(field, definition, live, expected, actual),
      });
    }
  }

  // Immaterial differences are still REPORTED — the detail view marks them, in
  // a colour that is not the drift red — but they do not bucket a key as
  // drifted and they do not stop Update. A row whose only difference is a
  // stray JSON blob on a String definition is in sync for every purpose that
  // matters, and filing it under Drifted would bury the rows that are not.
  const material = differences.filter((difference) => difference.material);

  return {
    manifest: definition,
    live,
    differences,
    unknownFields,
    canUpdate: material.every((difference) => difference.writable),
  };
}

/** The differences that decide the bucket and drive Update. */
export function materialDifferences(
  differences: readonly FieldDifference[]
): readonly FieldDifference[] {
  return differences.filter((difference) => difference.material);
}

/**
 * Whether this organization has set its own value for a key.
 *
 * Either of `value`/`valueJSON` being non-null is the signal — the values query
 * is asked for stored records only (`includeDefaults: false`), so a record
 * existing at all already means someone wrote one. Both are checked because a
 * JSON-typed config stores in the second and would otherwise read as untouched.
 */
export function hasCustomValue(live: LiveDefinition | undefined): boolean {
  return !isNil(live?.value) || !isNil(live?.valueJSON);
}

export function diffDefinitions(
  manifest: ConfigDefinitionManifest,
  liveRecords: readonly LiveDefinition[]
): DefinitionDiff {
  // First record wins; duplicates are reported rather than silently merged.
  const byKey = new Map<string, LiveDefinition>();
  const duplicateKeys: string[] = [];
  for (const record of liveRecords) {
    if (byKey.has(record.configKey)) {
      if (!duplicateKeys.includes(record.configKey)) {
        duplicateKeys.push(record.configKey);
      }
      continue;
    }
    byKey.set(record.configKey, record);
  }

  const missing: ManifestDefinition[] = [];
  const inSync: ComparedDefinition[] = [];
  const drifted: ComparedDefinition[] = [];
  const claimed = new Set<string>();
  const tierByKey: Record<string, DefinitionTier> = {};
  const tiered: [readonly ManifestDefinition[], DefinitionTier][] = [
    [manifest.definitions, 'standard'],
    [manifest.advanced, 'advanced'],
    [manifest.developer, 'developer'],
  ];

  for (const [definitions, tier] of tiered) {
    for (const definition of definitions) {
      tierByKey[definition.configKey] = tier;
    }
  }

  for (const definition of tiered.flatMap(([definitions]) => definitions)) {
    claimed.add(definition.configKey);
    const live = byKey.get(definition.configKey);
    if (!live) {
      missing.push(definition);
      continue;
    }
    const compared = compare(definition, live);
    // Material only — see `isMaterial`. An immaterial difference is carried on
    // the record and marked in the detail view, but it does not move the key.
    (materialDifferences(compared.differences).length > 0
      ? drifted
      : inSync
    ).push(compared);
  }

  const deprecatedPresent: DeprecatedPresent[] = [];
  for (const entry of manifest.deprecated) {
    claimed.add(entry.configKey);
    const live = byKey.get(entry.configKey);
    if (live) {
      deprecatedPresent.push({ entry, live });
    }
  }

  const unrecognized: LiveDefinition[] = [];
  for (const [key, record] of byKey) {
    if (!claimed.has(key)) {
      unrecognized.push(record);
    }
  }

  return {
    tierByKey,
    missing,
    inSync,
    drifted,
    deprecatedPresent,
    unrecognized,
    duplicateKeys,
  };
}
